import { CONNECTOR_LEVELS } from '@avalara/bat-apps-shared/constants';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { Connector } from '@avalara/types/index';

import { getPartnerSignupConnectorByCode } from '@avalara/bat-apps-shared/constants';
import analytics from '../lib/analytics';

import type { AccountSetupDetails } from '@avalara/types/index';
import type { PrefilledSignupParams } from '@avalara/types/index';
import type { AvaTaxSignupState } from '@avalara/types/reducer-states';

const initialState: AvaTaxSignupState = {
  selectedConnector: null,
  partnerSignup: null,
  isSoho: false,
  search: {
    connectorsSearchResults: [],
    connectorSearchString: '',
  },
  success: false,
  accountId: null,
  licenseKey: '',
  buyComplete: false,
  magicLink: '',
  magicLinkExpirationTime: 0,
};

const store = createSlice({
  name: 'avataxSignup',
  initialState,
  reducers: {
    receivePrefilledPartnerDetails: (
      state,
      action: PayloadAction<{
        prefilledData: PrefilledSignupParams;
        connectorFromQuery?: Connector;
      }>,
    ) => {
      const { prefilledData, connectorFromQuery } = action.payload;
      const { partnerCode, returnUrlOverride } = prefilledData;

      const maybePartnerInfo = getPartnerSignupConnectorByCode(partnerCode);
      if (!maybePartnerInfo) return;

      const { partnerInfo } = maybePartnerInfo;
      const selectedConnector = state.selectedConnector || connectorFromQuery;

      if (selectedConnector == null) return;

      const isValidPartnerUrl =
        window.appEnv !== 'prod' &&
        returnUrlOverride &&
        partnerInfo.allowedDevUrls &&
        partnerInfo.allowedDevUrls.includes(returnUrlOverride);

      const partnerReturnUrl = (isValidPartnerUrl && returnUrlOverride) || partnerInfo.returnUrl;

      state.selectedConnector = selectedConnector;
      state.partnerSignup = {
        name: selectedConnector.name,
        code: partnerCode,
        returnUrl: partnerReturnUrl,
      };
    },

    createAvataxAccountSuccess: (state, action: PayloadAction<number>) => {
      if (state.selectedConnector && window.localStorage) {
        window.localStorage.setItem(`ava-connector-id-signup-for-${action.payload}`, state.selectedConnector.id);
      }
      state.success = true;
      state.accountId = action.payload;
    },

    receiveAccountInitializationDetails: (state, action: PayloadAction<AccountSetupDetails>) => {
      state.success = true;
      state.accountId = action.payload.accountId;
      state.licenseKey = action.payload.licenseKey;
      state.magicLink = action.payload.magicLink;
      state.magicLinkExpirationTime = action.payload.expiresAt;
    },

    showConnectorDetails: (state, { payload: selectedConnector }: PayloadAction<Connector>) => {
      analytics.track('SHOW_CONNECTOR_DETAILS', { connector: selectedConnector.name });

      const metadata = selectedConnector.level ? CONNECTOR_LEVELS[selectedConnector.level] : undefined;
      const augmentedConnector = { ...selectedConnector, metadata };

      state.selectedConnector = augmentedConnector;
      state.search.connectorsSearchResults = [];
      state.search.connectorSearchString = selectedConnector.name;
    },

    deselectConnector: (state) => {
      analytics.track('CONNECTOR_NOT_FOUND', { connectorString: state.search.connectorSearchString });
      state.selectedConnector = null;
      state.search.connectorsSearchResults = [];
      state.search.connectorSearchString = '';
    },

    searchConnectors: (
      state,
      action: PayloadAction<{
        connectorsSearchResults: Array<Connector>;
        connectorSearchString: string;
      }>,
    ) => {
      state.search.connectorsSearchResults = action.payload.connectorsSearchResults;
      state.search.connectorSearchString = action.payload.connectorSearchString;
    },

    toggleIsSoho: (state, action: PayloadAction<string>) => {
      const searchParams = new URLSearchParams(action.payload);
      state.isSoho = (searchParams.get('soho') || '').toUpperCase() === 'TRUE';
    },

    setBuySuccess: (state) => {
      state.buyComplete = true;
    },
  },
});

export const {
  receivePrefilledPartnerDetails,
  createAvataxAccountSuccess,
  receiveAccountInitializationDetails,
  showConnectorDetails,
  deselectConnector,
  searchConnectors,
  toggleIsSoho,
  setBuySuccess,
} = store.actions;

export default store;
